(() => {
  'use strict';

  const ENDPOINT = 'https://oomyrntkxroebztukntp.supabase.co/functions/v1/maison-yanna-reviews';
  const summaryCache = new Map();
  const productCache = new Map();

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  function stars(value, empty = false) {
    const rounded = empty ? 0 : Math.max(0, Math.min(5, Math.round(Number(value) || 0)));
    let out = '';
    for (let i = 1; i <= 5; i += 1) out += i <= rounded ? '★' : '☆';
    return out;
  }

  function formatDate(value) {
    if (!value) return '';
    try {
      return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(value));
    } catch (_) {
      return '';
    }
  }

  async function request(url, options) {
    const response = await fetch(url, { credentials: 'omit', ...options });
    let body = {};
    try { body = await response.json(); } catch (_) {}
    if (!response.ok || body.ok === false) throw new Error(body.error || 'Une erreur est survenue.');
    return body;
  }

  async function getProduct(productId) {
    const id = String(productId || '');
    if (!id) return null;
    if (!productCache.has(id)) {
      productCache.set(id, request(`${ENDPOINT}?action=product&product_id=${encodeURIComponent(id)}&limit=30`));
    }
    return productCache.get(id);
  }

  async function loadSummaryBatch(ids) {
    const missing = [...new Set(ids.map(String).filter(Boolean))].filter((id) => !summaryCache.has(id));
    const chunks = [];
    for (let i = 0; i < missing.length; i += 70) chunks.push(missing.slice(i, i + 70));

    await Promise.all(chunks.map(async (chunk) => {
      const body = await request(`${ENDPOINT}?action=summaries&product_ids=${encodeURIComponent(chunk.join(','))}`);
      const found = new Set();
      (body.summaries || []).forEach((row) => {
        const id = String(row.shopify_product_id);
        found.add(id);
        summaryCache.set(id, {
          average_rating: Number(row.average_rating || 0),
          review_count: Number(row.review_count || 0)
        });
      });
      chunk.forEach((id) => {
        if (!found.has(id)) summaryCache.set(id, { average_rating: 0, review_count: 0 });
      });
    }));
  }

  function paintBadge(el, summary) {
    const count = Number(summary?.review_count || 0);
    const average = Number(summary?.average_rating || 0);
    const onCollection = document.body.dataset.myCollectionReviews === 'hide-empty';
    const showEmpty = !onCollection && el.dataset.showEmpty === 'true';
    el.dataset.reviewCount = String(count);
    if (!count && !showEmpty) {
      el.hidden = true;
      return;
    }

    const single = el.dataset.singleStar === 'true';
    const displayMode = el.dataset.displayMode || 'rating';
    const visual = single ? (count ? '★' : '☆') : stars(average, !count);
    if (displayMode === 'premium') {
      const language = document.documentElement.lang || 'fr';
      const formattedAverage = new Intl.NumberFormat(language, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(average);
      const caption = count ? `${formattedAverage}/5 · ${count} ${el.dataset.reviewsLabel || 'avis'}` : (el.dataset.emptyLabel || 'Aucun avis');
      const fill = count ? Math.max(0, Math.min(100, average * 20)) : 0;
      el.innerHTML = `<span class="my-review-badge__meter" aria-hidden="true"><span>☆☆☆☆☆</span><span class="my-review-badge__fill" style="width:${fill}%">★★★★★</span></span><span class="my-review-badge__text">${escapeHtml(caption)}</span>`;
      el.setAttribute('aria-label', caption);
      el.hidden = false;
      return;
    }
    let text = '';
    if (displayMode === 'summary') text = '';
    else if (displayMode === 'count') text = `(${count})`;
    else text = `(${count ? average.toFixed(1) : '0.0'})`;

    el.innerHTML = displayMode === 'summary'
      ? `<span class="my-review-badge__stars" aria-hidden="true">${visual}</span>`
      : `<span class="my-review-badge__stars" aria-hidden="true">${visual}</span><span class="my-review-badge__text">${escapeHtml(text)}</span>`;
    el.setAttribute('aria-label', count ? `${average.toFixed(1)} sur 5, ${count} avis` : 'Aucun avis');
    el.hidden = false;
  }

  async function loadBadges() {
    const badges = [...document.querySelectorAll('[data-my-review-rating]')];
    if (!badges.length) return;
    const ids = badges.map((el) => el.dataset.productId).filter(Boolean);
    try {
      await loadSummaryBatch(ids);
      badges.forEach((el) => paintBadge(el, summaryCache.get(String(el.dataset.productId))));
    } catch (_) {
      badges.forEach((el) => { if (document.body.dataset.myCollectionReviews === 'hide-empty' || el.dataset.showEmpty !== 'true') el.hidden = true; });
    }
  }

  function renderInline(el, summary) {
    if (!el) return;
    const count = Number(summary?.review_count || 0);
    const average = Number(summary?.average_rating || 0);
    el.innerHTML = count
      ? `<a class="my-review-inline-link" href="#avis-maison-yanna"><span class="my-review-stars" aria-hidden="true">${stars(average)}</span><span class="my-review-inline-link__text">${average.toFixed(1)}/5 · ${count} avis Maison Yanna</span></a>`
      : `<a class="my-review-inline-link" href="#avis-maison-yanna"><span class="my-review-stars" aria-hidden="true">☆☆☆☆☆</span><span class="my-review-inline-link__text">Soyez la première à donner votre avis</span></a>`;
  }

  function createInlineIfNeeded(productRoot) {
    let el = document.querySelector('[data-my-review-inline]');
    if (el) return el;
    const title = document.querySelector('h1.product-title, h1.my4c-title, main h1');
    if (!title) return null;
    el = document.createElement('div');
    el.className = 'my-review-inline';
    el.dataset.myReviewInline = '';
    title.insertAdjacentElement('afterend', el);
    return el;
  }

  function reviewCard(review) {
    const verified = review.verified_purchase ? '<span class="my-product-review-card__verified">✓ Achat vérifié</span>' : '';
    const title = review.review_title ? `<h3>${escapeHtml(review.review_title)}</h3>` : '';
    const date = formatDate(review.published_at || review.created_at);
    return `
      <article class="my-product-review-card">
        <div class="my-product-review-card__top">
          <span class="my-product-review-card__stars" aria-label="${Number(review.rating)} étoiles sur 5">${stars(review.rating)}</span>
          ${verified}
        </div>
        ${title}
        <p class="my-product-review-card__body">${escapeHtml(review.review_body)}</p>
        <div class="my-product-review-card__meta"><strong>${escapeHtml(review.author_name || 'Cliente Maison Yanna')}</strong><span>${escapeHtml(date)}</span></div>
      </article>`;
  }

  function renderProductReviews(root, payload) {
    const summary = payload.summary || { average_rating: 0, review_count: 0 };
    const reviews = payload.reviews || [];
    const count = Number(summary.review_count || 0);
    const average = Number(summary.average_rating || 0);
    const summaryEl = root.querySelector('[data-my-review-section-summary]');
    const list = root.querySelector('[data-my-review-list]');

    if (summaryEl) {
      summaryEl.innerHTML = count
        ? `<span class="my-review-stars" aria-hidden="true">${stars(average)}</span><strong>${average.toFixed(1)}/5</strong><small>${count} avis client${count > 1 ? 's' : ''}</small>`
        : `<span class="my-review-stars" aria-hidden="true">☆☆☆☆☆</span><strong>—</strong><small>Aucun avis publié pour le moment</small>`;
    }

    if (list) {
      list.innerHTML = reviews.length
        ? reviews.map(reviewCard).join('')
        : '<div class="my-product-reviews__empty">Vous avez testé ce produit ? Soyez la première personne à partager votre expérience avec la communauté Maison Yanna.</div>';
    }

    const inline = createInlineIfNeeded(root);
    renderInline(inline, summary);
  }

  function setupStarPicker(form) {
    const input = form.querySelector('input[name="rating"]');
    const buttons = [...form.querySelectorAll('.my-review-star-choice')];
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const rating = Number(button.dataset.rating || 0);
        input.value = String(rating);
        buttons.forEach((b) => {
          const active = Number(b.dataset.rating || 0) <= rating;
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-checked', Number(b.dataset.rating || 0) === rating ? 'true' : 'false');
        });
      });
    });
  }

  function setupForm(root) {
    const form = root.querySelector('[data-my-review-form]');
    if (!form || form.dataset.ready === 'true') return;
    form.dataset.ready = 'true';
    setupStarPicker(form);

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const feedback = form.querySelector('[data-my-review-feedback]');
      const submit = form.querySelector('button[type="submit"]');
      const data = Object.fromEntries(new FormData(form).entries());
      data.consent = form.querySelector('input[name="consent"]')?.checked === true;

      feedback.className = 'my-product-reviews__feedback';
      if (!data.rating) {
        feedback.textContent = 'Choisissez une note de 1 à 5 étoiles.';
        feedback.classList.add('is-error');
        return;
      }
      if (!form.reportValidity()) return;

      submit.disabled = true;
      submit.textContent = 'Envoi…';
      feedback.textContent = '';

      try {
        const result = await request(ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        });
        feedback.textContent = result.message || 'Merci 🤍 Votre avis a bien été reçu.';
        feedback.classList.add('is-success');
        const productId = data.product_id;
        const email = form.querySelector('input[name="author_email"]')?.value || '';
        form.reset();
        form.querySelector('input[name="product_id"]').value = productId;
        if (email) form.querySelector('input[name="author_email"]').value = email;
        form.querySelectorAll('.my-review-star-choice').forEach((b) => {
          b.classList.remove('is-active');
          b.setAttribute('aria-checked', 'false');
        });
        root.querySelector('[data-my-review-form-wrap]')?.setAttribute('open', '');
      } catch (error) {
        feedback.textContent = error.message || 'Une erreur est survenue. Réessayez dans un instant.';
        feedback.classList.add('is-error');
      } finally {
        submit.disabled = false;
        submit.textContent = 'Envoyer mon avis';
      }
    });
  }

  async function loadProductPage() {
    const root = document.querySelector('[data-my-product-reviews]');
    if (!root) return;

    // Place reviews immediately after the main product section, before recommendations.
    const title = document.querySelector('h1.my4c-title, h1.product-title, main h1');
    const productSection = title ? title.closest('.shopify-section') : null;
    if (productSection && root.previousElementSibling !== productSection) {
      productSection.insertAdjacentElement('afterend', root);
    }

    setupForm(root);
    try {
      const payload = await getProduct(root.dataset.productId);
      renderProductReviews(root, payload || {});
    } catch (_) {
      const summaryEl = root.querySelector('[data-my-review-section-summary]');
      const list = root.querySelector('[data-my-review-list]');
      if (summaryEl) summaryEl.innerHTML = '<small>Les avis sont momentanément indisponibles.</small>';
      if (list) list.innerHTML = '<div class="my-product-reviews__empty">Impossible de charger les avis pour le moment. Vous pouvez néanmoins envoyer le vôtre ci-dessous.</div>';
    }
  }

  async function loadHomepageReviews() {
    const root = document.querySelector('[data-my-home-reviews]');
    if (!root) return;
    try {
      const payload = await request(`${ENDPOINT}?action=latest&limit=4`);
      const reviews = payload.reviews || [];
      const summary = payload.summary || {};
      const summaryEl = root.querySelector('[data-my-home-review-summary]');
      if (summaryEl) {
        const count = Number(summary.review_count || 0);
        const avg = Number(summary.average_rating || 0);
        summaryEl.innerHTML = count
          ? `<span class="my-reviews-v26__stars" aria-hidden="true">${stars(avg)}</span><span class="my-reviews-v26__rating-text">${avg.toFixed(1)} / 5 · ${count} avis Maison Yanna</span>`
          : '<span class="my-reviews-v26__rating-text">Avis Maison Yanna</span>';
      }

      const slots = [...root.querySelectorAll('[data-my-home-review]')];
      slots.forEach((slot, index) => {
        const review = reviews[index];
        if (!review) { slot.hidden = true; return; }
        slot.hidden = false;
        slot.querySelector('[data-stars]').textContent = stars(review.rating);
        const quote = slot.querySelector('[data-quote]');
        if (quote) quote.textContent = `« ${review.review_body} »`;
        const author = slot.querySelector('[data-author]');
        if (author) author.textContent = review.author_name || 'Cliente Maison Yanna';
        const product = slot.querySelector('[data-product]');
        if (product) {
          product.textContent = review.product_title || 'Maison Yanna';
          if (review.product_handle) product.setAttribute('href', `/products/${review.product_handle}`);
          else product.removeAttribute('href');
        }
        const verified = slot.querySelector('[data-verified]');
        if (verified) verified.hidden = !review.verified_purchase;
        slot.classList.add('is-visible');
      });
    } catch (_) {
      // Keep the server-rendered fallback reviews visible.
    }
  }

  function init() {
    loadBadges();
    loadProductPage();
    loadHomepageReviews();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  document.addEventListener('my:reviews:refresh', () => { loadBadges(); });
  document.addEventListener('shopify:section:load', () => setTimeout(init, 0));
})();

